import { fmtNum, fmtPct, fmtMin } from "../utils/format";

export function summarizeResponseOrig(data, question, mode = "general") {
  if (!data || !data.summary) return "No summary available.";

  const q = (question || "").toLowerCase();
  const ctx = data.context || {};
  const s = data.summary || {};
  const byMachine = Array.isArray(data.by_machine) ? data.by_machine : [];
  const reasons = Array.isArray(data.top_downtime_reasons) ? data.top_downtime_reasons : [];

  const when = ctx.date || ctx.dateStart || "this shift";
  const shift = ctx.shiftName || "Shift";

  const machineBits = (() => {
    if (!byMachine.length) return "";
    const sortedByUtil = [...byMachine]
      .filter(m => typeof m.utilization_pct === "number")
      .sort((a, b) => b.utilization_pct - a.utilization_pct);
    const top = sortedByUtil[0];
    const bottom = sortedByUtil[sortedByUtil.length - 1];
    let text = "";
    if (top) {
      text += ` Best performer: ${top.machineId} at ${fmtPct(top.utilization_pct)}`;
      if (top.part_count_total) text += ` with ${fmtNum(top.part_count_total)} parts`;
      text += `.`;
    }
    if (bottom && bottom.machineId !== (top && top.machineId)) {
      text += ` Lowest: ${bottom.machineId} at ${fmtPct(bottom.utilization_pct)}`;
      if (bottom.part_count_total) text += ` with ${fmtNum(bottom.part_count_total)} parts`;
      text += `.`;
    }
    return text;
  })();

  const reasonsBits = (() => {
    if (!reasons.length) return "";
    const topK = reasons.slice(0, 3);
    const list = topK.map(r => `${r.reason} (${fmtMin(r.minutes)})`).join(", ");
    return ` Top downtime reasons: ${list}.`;
  })();

  if (mode === "downtime") {
    let text = `On ${when}, ${shift} downtime totaled ${fmtMin(s.downtime_minutes)}.`;
    if (s.unlabeled_minutes != null) text += ` Unlabeled downtime was ${fmtMin(s.unlabeled_minutes)}.`;
    return text + reasonsBits;
  }

  const asksParts = q.includes("part count") || q.includes("parts") || q.includes("production");
  const asksUtil = q.includes("utilization") || q.includes("utilisation");

  let head = `On ${when}, ${shift} had ${fmtNum(s.machines_reporting)} machines reporting.`;

  if (asksParts) {
    head += ` Total part count was ${fmtNum(s.part_count_total)}.`;
    head += ` Average utilization was ${fmtPct(s.utilization_pct)}.`;
    const topByParts = [...byMachine]
      .filter(m => typeof m.part_count_total === "number")
      .sort((a, b) => (b.part_count_total || 0) - (a.part_count_total || 0))
      .slice(0, 3);
    if (topByParts.length) {
      head += ` Top producers: ` + topByParts.map(m => `${m.machineId} (${fmtNum(m.part_count_total)} parts)`).join(", ") + `.`;
    }
    return head + reasonsBits;
  }

  if (asksUtil) {
    head += ` Average utilization was ${fmtPct(s.utilization_pct)} across ${fmtNum(s.machines_reporting)} machines, with ${fmtNum(s.part_count_total)} total parts.`;
    return head + machineBits + reasonsBits;
  }

  head += ` Average utilization was ${fmtPct(s.utilization_pct)}, total part count ${fmtNum(s.part_count_total)}.`;
  head += ` Total downtime ${fmtMin(s.downtime_minutes)}`;
  if (s.unlabeled_minutes != null) head += ` (unlabeled ${fmtMin(s.unlabeled_minutes)})`;
  head += `.`;
  return head + machineBits + reasonsBits;
}

export function summarizeMachineStateRow(row) {
  if (!row) return "I couldn’t find that machine in the current shift.";
  // status: "on" | "off", status_duration_minutes: number
  const name = row.machineId || "machine";
  const mins = Number(row.status_duration_minutes || 0);
  const h = Math.floor(mins / 60);
  const m = mins % 60;

  const humanDur =
    mins < 60 ? `${m} min` :
      m === 0 ? `${h} hr${h === 1 ? "" : "s"}` :
        `${h} hr${h === 1 ? "" : "s"} ${m} min`;

  // If your backend uses different wording, adjust here
  const running = String(row.status || "").toLowerCase() === "on";
  const stateWord = running ? "in production" : "stopped";
  return `${name} has been ${stateWord} for ${humanDur}.`;
}

// NEW: list formatter for "Which machines are running/stopped?" and "show current status"
export function summarizeMachineStatusList(data, question = "", shape = {}) {
  const rows = Array.isArray(data?.by_machine) ? data.by_machine : [];
  if (!rows.length) return "No machines found for the current shift.";

  // Decide filter based on question and/or shape.machineId
  const q = (question || "").toLowerCase();
  const wantRunning = /\b(running|on|in production)\b/.test(q);
  const wantStopped = /\b(stopped|off|down)\b/.test(q);

  // shape.machineId: "" (filter based on question), "all" (no filter)
  const filterMode = (shape?.machineId || "").toLowerCase().trim();
  const applyFilter =
    filterMode === "all" ? "all" :
      wantRunning ? "on" :
        wantStopped ? "off" :
          "all";

  // Normalize + filter
  const normalized = rows.map(r => ({
    machineId: r.machineId || r.machine || "UNKNOWN",
    status: (r.status || "").toLowerCase(), // "on" | "off"
    duration: Number(r.status_duration_minutes || 0)
  }));

  const filtered = normalized.filter(r => {
    if (applyFilter === "all") return true;
    return r.status === applyFilter;
  });

  if (!filtered.length) {
    if (applyFilter === "on") return "No machines are currently running.";
    if (applyFilter === "off") return "No machines are currently stopped.";
    return "No machines found for the current shift.";
  }

  // Sort: running first (or by duration desc), then name
  filtered.sort((a, b) => b.duration - a.duration || a.machineId.localeCompare(b.machineId));

  // Format duration as "2h 3m" or "37 min"
  const fmtDur = (mins) => {
    const m = Math.max(0, Math.round(mins || 0));
    const h = Math.floor(m / 60);
    const r = m % 60;
    if (m < 60) return `${r} min`;
    if (r === 0) return `${h} hr${h === 1 ? "" : "s"}`;
    return `${h} hr${h === 1 ? "" : "s"} ${r} min`;
  };

  // Build simple list: "<machine> — <status> (<duration>)"
  const lines = filtered.map(r => {
    const label = r.status === "on" ? "on" : "off";
    return `${r.machineId} — ${label} (${fmtDur(r.duration)})`;
  });

  // Optional header to make it clear (comment out if you want only lines)
  if (applyFilter === "on") return `Running machines:\n` + lines.join("\n");
  if (applyFilter === "off") return `Stopped machines:\n` + lines.join("\n");
  return `Current machine status:\n` + lines.join("\n");
}




export function summarizeResponse(data, question, mode = "general") {
  if (!data || !data.summary) return "No summary available.";

  const q = (question || "").toLowerCase();
  const ctx = data.context || {};
  const s = data.summary || {};
  const byMachine = Array.isArray(data.by_machine) ? data.by_machine : [];
  const reasons = Array.isArray(data.top_downtime_reasons) ? data.top_downtime_reasons : [];

  const when = ctx.date || ctx.dateStart || "this shift";
  const shift = ctx.shiftName || "Shift";

  // Helpers
  const topByParts = [...byMachine]
    .filter(m => typeof m.part_count_total === "number" && m.part_count_total > 0)
    .sort((a, b) => (b.part_count_total || 0) - (a.part_count_total || 0))
    .slice(0, 3);

  const byUtil = [...byMachine].filter(m => typeof m.utilization_pct === "number");
  byUtil.sort((a, b) => b.utilization_pct - a.utilization_pct);
  const bestUtil = byUtil[0];
  const worstUtil = byUtil.length > 1 ? byUtil[byUtil.length - 1] : null;

  const reasonLines = reasons.slice(0, 5).map(r => `• ${r.reason} — ${fmtMin(r.minutes)}`);

  // --- DOWNTIME-ONLY MODE ---
  if (mode === "downtime") {
    const lines = [
      `📅 ${when} — ${shift}`,
      "",
      `Downtime: ${fmtMin(s.downtime_minutes)}` +
        (s.unlabeled_minutes != null ? `  |  Unlabeled: ${fmtMin(s.unlabeled_minutes)}` : ""),
      reasonLines.length ? "" : null,
      reasonLines.length ? "Top downtime reasons:" : null,
      ...reasonLines
    ].filter(Boolean);
    return lines.join("\n");
  }

  // Intent hints from the question
  const asksParts = q.includes("part count") || q.includes("parts") || q.includes("production");
  const asksUtil = q.includes("utilization") || q.includes("utilisation");

  // --- GENERAL MODE ---
  const header = `📅 ${when} — ${shift}`;
  const kpis = [
    `Machines reporting: ${fmtNum(s.machines_reporting)}`,
    `Utilization: ${fmtPct(s.utilization_pct)}`,
    `Part count: ${fmtNum(s.part_count_total)}`
  ].join("  |  ");

  const producersBlock =
    topByParts.length
      ? ["", "Top producers:", ...topByParts.map(m =>
          `• ${m.machineId} — ${fmtNum(m.part_count_total)} parts` +
          (typeof m.utilization_pct === "number" ? ` @ ${fmtPct(m.utilization_pct)}` : "")
        )]
      : [];

  const utilBlock = [];
  if (bestUtil) {
    utilBlock.push("", "Utilization spread:");
    utilBlock.push(`• Best — ${bestUtil.machineId} @ ${fmtPct(bestUtil.utilization_pct)}` +
                   (bestUtil.part_count_total ? ` (${fmtNum(bestUtil.part_count_total)} parts)` : ""));
  }
  if (worstUtil && worstUtil.machineId !== (bestUtil && bestUtil.machineId)) {
    utilBlock.push(`• Lowest — ${worstUtil.machineId} @ ${fmtPct(worstUtil.utilization_pct)}` +
                   (worstUtil.part_count_total ? ` (${fmtNum(worstUtil.part_count_total)} parts)` : ""));
  }

  const downtimeBlock = [
    "",
    `Downtime: ${fmtMin(s.downtime_minutes)}` +
      (s.unlabeled_minutes != null ? `  |  Unlabeled: ${fmtMin(s.unlabeled_minutes)}` : "")
  ];
  if (reasonLines.length) {
    downtimeBlock.push("Top downtime reasons:");
    downtimeBlock.push(...reasonLines);
  }

  // If the question specifically asked for parts/util only, we can emphasize that
  const lines = [header, "", kpis];

  if (asksParts) {
    lines.push(...producersBlock);
  } else if (asksUtil) {
    lines.push(...utilBlock);
  } else {
    // Default: include both highlights
    lines.push(...producersBlock, ...utilBlock);
  }

  lines.push(...downtimeBlock);

  return lines.filter(Boolean).join("\n");
}


